/**
 * Start a multi-layer thickness analysis (LAYER_STACK).
 *
 * A construction stack is N surface LEVELS (top of wearing course, top of
 * binder, … formation) giving N−1 LAYERS of material between them. The table
 * this dialog renders is one row per LEVEL with the layers drawn in the gaps
 * — see `LayerStackFields`. The dialog never asks how many layers there are.
 *
 * Structure follows `RunSurfaceDifferenceDialog`: the same four-hook pure
 * model spread into the schema, the same optional station-volumes section,
 * the same "re-read the gate at the payload" rule.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, useRef } from 'react';
import { useFieldArray, useForm, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { alignmentsAtom } from '@/atoms/alignment.atom';
import { deviationTrackingAtom } from '@/atoms/deviation-progress.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { execute } from '@/graphql/execute';
import type { LayersQuery } from '@/graphql/graphql';
import { useFeature } from '@/hooks/useFeature';
import {
  blankLevel,
  deriveLayerStack,
  estimateLayerStack,
  layerStackDefaults,
  layerStackPayload,
  layerStackPreflight,
  layerStackZodShape,
  nextLevelIndex,
  refineLayerStack,
  type LayerStackFormValues,
} from '@/lib/layerStack/dialogModel';
import {
  DEFAULT_MIN_COVERAGE_PCT,
  deriveStationVolumes,
  formatSectionBytes,
  MAX_CROSSFALL_TOLERANCE_PCT,
  MAX_SECTION_BYTES,
  MIN_CROSSFALL_BAND_WIDTH_M,
  MIN_CROSSFALL_TOLERANCE_PCT,
  refineStationVolumes,
  stationVolumesDefaults,
  stationVolumesPayload,
  stationVolumesZodShape,
  type StationVolumesFormValues,
} from '@/lib/stationVolumes/dialogModel';
import { useDialog } from '@/providers/DialogProvider';
import { startLayerStack } from '@/queries/layerStack.query';

import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';
import { LayerStackFields } from '@/components/LayerStackFields';
import { StationVolumesFields } from '@/components/StationVolumesFields';

type LayerData = LayersQuery['layers'][number];

const schema = z
  .object({ ...layerStackZodShape, ...stationVolumesZodShape })
  .superRefine((d, ctx) => {
    refineLayerStack(d, ctx);
    refineStationVolumes(d, ctx);
  });
export type LayerStackFormData = z.infer<typeof schema>;

interface Props {
  projectId: string;
  candidateLayers: LayerData[];
  /** LEVELS, not layers. Two is the smallest stack that describes anything. */
  initialLevelCount?: number;
}

export function RunLayerStackDialog({ projectId, candidateLayers, initialLevelCount = 2 }: Props) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();
  const alignments = useAtomValue(alignmentsAtom);
  const layers = useAtomValue(layersAtom);
  const setTrackers = useSetAtom(deviationTrackingAtom);
  const gate = useFeature('analysis.layerStack');
  const stationGate = useFeature('analysis.stationVolumes');
  /** Layer ids as they stood when Run was clicked — see `onSuccess`. A ref,
   *  not state: it is written in the mutation and read by it, never rendered. */
  const existingStackLayerIds = useRef<string[]>([]);

  const sorted = useMemo(() => [...candidateLayers].sort((a, b) => a.name.localeCompare(b.name)), [candidateLayers]);

  const form = useForm<LayerStackFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...layerStackDefaults({
        levelCount: initialLevelCount,
        nameFor: (i) => t('layerStack.dialog.levelFallbackName', { n: i + 1 }),
        // Seed each level's DESIGN picker from the candidate list where one
        // exists, the same courtesy `RunSurfaceDifferenceDialog` does for
        // top/bottom. Levels past the end of the list start blank rather than
        // repeating a guess.
        designLayerIdFor: (i) => sorted[i]?.id ?? '',
        name: t('layerStack.dialog.defaultOutputName'),
      }),
      ...stationVolumesDefaults(undefined, alignments, stationGate.allowed),
    },
  });

  // `fields` is taken, not just `append`/`remove`: its `id` is the only stable
  // identity a level has, and `LayerStackFields` keys the rows by it. Keyed by
  // POSITION instead, removing a middle level leaves React reusing the row
  // component that was there — and every input in it keeps the removed level's
  // text (`NumericField` holds a local string buffer by design) while
  // react-hook-form's array has correctly shifted. The boxes then show one
  // stack and Run submits another.
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'levels' });

  // `form.watch()` in the component that OWNS the form (the same thing the
  // other two run dialogs do). `useWatch` is deliberately not used for
  // `levels`: its subscription does not fire for a `useFieldArray` insert or
  // remove, so a removed level stayed on screen with its layer gap intact —
  // which is precisely the arithmetic this dialog exists to get right.
  const levels = (form.watch('levels') ?? []) as LayerStackFormValues['levels'];
  const resolution = form.watch('resolution');
  const outputName = form.watch('name');

  // Recomputed every render, deliberately NOT memoised on `levels`:
  // `form.watch()` hands back react-hook-form's own values object, which it
  // MUTATES IN PLACE for a per-field edit. A `useMemo` keyed on that array
  // therefore never invalidates when the user types, and the derived errors
  // freeze at their first-render values. Both functions are O(levels) over at
  // most `MAX_LEVELS` entries.
  const stackValues: LayerStackFormValues = { name: outputName ?? '', resolution, levels };
  const derived = deriveLayerStack(stackValues);
  const warnings = layerStackPreflight(stackValues);

  const svValues: StationVolumesFormValues = {
    stationVolumesEnabled: form.watch('stationVolumesEnabled'),
    alignmentKey: form.watch('alignmentKey'),
    stationInterval: form.watch('stationInterval'),
    swathLeft: form.watch('swathLeft'),
    swathRight: form.watch('swathRight'),
    swathLinked: form.watch('swathLinked'),
    stationFromText: form.watch('stationFromText'),
    stationToText: form.watch('stationToText'),
    offsetBin: form.watch('offsetBin'),
    crossfallEnabled: form.watch('crossfallEnabled'),
    crossfallLeftEnabled: form.watch('crossfallLeftEnabled'),
    crossfallRightEnabled: form.watch('crossfallRightEnabled'),
    crossfallLeftFrom: form.watch('crossfallLeftFrom'),
    crossfallLeftTo: form.watch('crossfallLeftTo'),
    crossfallRightFrom: form.watch('crossfallRightFrom'),
    crossfallRightTo: form.watch('crossfallRightTo'),
    crossfallLinked: form.watch('crossfallLinked'),
    crossfallTolerancePct: form.watch('crossfallTolerancePct'),
  };
  const alignment = alignments.get(svValues.alignmentKey);
  const svDerived = useMemo(
    () => deriveStationVolumes(alignment, svValues, resolution),
    // Each field individually: `svValues` is a fresh object every render.
    [
      alignment,
      resolution,
      svValues.stationVolumesEnabled,
      svValues.stationInterval,
      svValues.swathLeft,
      svValues.swathRight,
      svValues.stationFromText,
      svValues.stationToText,
      svValues.offsetBin,
      // The crossfall fields the derivation reads. `crossfallLinked` is
      // deliberately absent for the same reason `swathLinked` is — it is a UI
      // affordance the derivation never looks at.
      svValues.crossfallEnabled,
      svValues.crossfallLeftEnabled,
      svValues.crossfallRightEnabled,
      svValues.crossfallLeftFrom,
      svValues.crossfallLeftTo,
      svValues.crossfallRightFrom,
      svValues.crossfallRightTo,
      svValues.crossfallTolerancePct,
    ],
  );

  /**
   * The stack's OWN section-size estimate. `deriveStationVolumes` sizes the
   * byproduct for a 2-plane run; a stack writes every level's as-built AND
   * every level's design — `2 * levelCount` planes — so the same corridor
   * costs `levelCount` times as much here (ruling R5). Blocking on the
   * station model's smaller number alone would let a six-level run through at
   * three times the cap.
   */
  const stackEstimate = svDerived.estimate
    ? estimateLayerStack({
        binCount: svDerived.estimate.binCount,
        nOffsets: svDerived.estimate.nOffsets,
        levelCount: derived.levelCount,
      })
    : null;
  const stackSectionTooBig =
    svValues.stationVolumesEnabled && !!stackEstimate && stackEstimate.sectionBytes > MAX_SECTION_BYTES;

  const addLevel = useCallback(() => {
    const nameFor = (i: number) => t('layerStack.dialog.levelFallbackName', { n: i + 1 });
    // The first FREE name, never `levels.length`: after a removal that number
    // is already on a row, and a duplicate name is refused by the validator —
    // dead-ending the dialog on a collision that looks like nothing at all.
    return append(blankLevel(nextLevelIndex(form.getValues('levels'), nameFor), nameFor));
  }, [append, form, t]);

  /** Tags → translated copy. The schema's `message`s are tags (`levelsRequired`),
   *  never English sentences — same convention the other two run dialogs use. */
  const ERROR_COPY: Record<string, string> = {
    levelsRequired: t('layerStack.errors.levelsRequired'),
    tooManyLevels: t('layerStack.errors.tooManyLevels'),
    resolutionPositive: t('layerStack.errors.resolutionPositive'),
    levelNameRequired: t('layerStack.errors.levelNameRequired'),
    duplicateLevelName: t('layerStack.errors.duplicateLevelName'),
    levelSurfaceRequired: t('layerStack.errors.levelSurfaceRequired'),
    toleranceNegative: t('layerStack.errors.toleranceNegative'),
    alignmentRequired: t('stationVolumes.dialog.alignmentRequired'),
    swathZero: t('stationVolumes.dialog.swathZero'),
    intervalRange: t('stationVolumes.errors.intervalRange'),
    swathRange: t('stationVolumes.errors.swathRange'),
    offsetBinRange: t('stationVolumes.errors.offsetBinRange'),
    crossfallBand: t('stationVolumes.errors.crossfallBandRange', { min: MIN_CROSSFALL_BAND_WIDTH_M }),
    crossfallWidth: t('stationVolumes.errors.crossfallBandRange', { min: MIN_CROSSFALL_BAND_WIDTH_M }),
    crossfallSwath: t('stationVolumes.errors.crossfallSwath'),
    crossfallNoBand: t('stationVolumes.errors.crossfallNoBand'),
    crossfallTolerance: t('stationVolumes.errors.crossfallTolerance', {
      min: MIN_CROSSFALL_TOLERANCE_PCT,
      max: MAX_CROSSFALL_TOLERANCE_PCT,
    }),
  };

  // Live inline copy, derived rather than read out of `formState.errors`:
  // react-hook-form only populates those after a submit attempt, and "you
  // need at least two levels" has to be on screen the moment the user
  // removes the second one.
  const stackErrors = [
    ...(derived.tooFewLevels ? [ERROR_COPY.levelsRequired] : []),
    ...(derived.tooManyLevels ? [ERROR_COPY.tooManyLevels] : []),
    ...(derived.resolutionInvalid ? [ERROR_COPY.resolutionPositive] : []),
    ...(stackSectionTooBig
      ? [
          t('layerStack.errors.sectionTooBig', {
            size: formatSectionBytes(stackEstimate?.sectionBytes ?? 0),
            max: formatSectionBytes(MAX_SECTION_BYTES),
          }),
        ]
      : []),
  ];
  const levelErrors = (i: number): string[] => [
    ...(derived.missingNameIndices.includes(i) ? [ERROR_COPY.levelNameRequired] : []),
    ...(derived.duplicateNameIndices.includes(i) ? [ERROR_COPY.duplicateLevelName] : []),
    ...(derived.noSurfaceIndices.includes(i) ? [ERROR_COPY.levelSurfaceRequired] : []),
    ...(derived.negativeToleranceIndices.includes(i) ? [ERROR_COPY.toleranceNegative] : []),
  ];

  const mutation = useMutation({
    mutationFn: (vars: LayerStackFormData) => {
      // Snapshot BEFORE the run starts: the tracker calls the run complete on
      // a LAYER_STACK layer for this batch that was not already there.
      existingStackLayerIds.current = layers.filter((l) => l.id).map((l) => l.id);
      const stack = layerStackPayload({ name: vars.name, resolution: vars.resolution, levels: vars.levels });
      return execute(startLayerStack, {
        input: {
          ...stack,
          // `stationGate.allowed` re-read here, not trusted from the form —
          // same rule as the payload gate below.
          stationVolumes: stationGate.allowed
            ? stationVolumesPayload(vars, svDerived, DEFAULT_MIN_COVERAGE_PCT)
            : undefined,
        },
      });
    },
    onSuccess: (batchId, vars) => {
      const name = vars.name || t('layerStack.dialog.defaultOutputName');
      // A PERSISTENT toast tracked to completion, never a fire-and-forget
      // "started" notice (CLAUDE.md). `useDeviationProgress` resolves it on
      // this batch's ONE result layer appearing with the batch's POST job
      // PROCESSED — which is what postprocess now mints (spec §6.2). Task 10
      // could not do this because no layer was ever created and the toast
      // would have spun for ever.
      const toastId = toast.loading(t('layerStack.dialog.runningToast', { name }), { duration: Infinity });
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId: String(batchId),
          projectId,
          // A stack has N source surfaces, so there is no single source
          // layer: the tracker matches on the batch id alone (see
          // `DeviationTracker.kind`). The name is the ANALYSIS's, which is
          // what the toast copy names.
          sourceLayerId: '',
          sourceLayerName: name,
          existingDerivedLayerIds: existingStackLayerIds.current,
          kind: 'layerStack',
        },
      ]);
      queryClient.invalidateQueries({ queryKey: ['projectJobs', projectId] });
      dismissActiveDialog();
    },
    onError: (err: Error) => toast.error(`${t('layerStack.dialog.failedToStart')}: ${err.message}`),
  });

  const onInvalid = (errors: FieldErrors<LayerStackFormData>) => {
    const first = Object.values(errors)
      .flatMap((e) => (Array.isArray(e) ? e : [e]))
      .flatMap((e) => (e && typeof e === 'object' ? Object.values(e as Record<string, unknown>) : []))
      .concat(Object.values(errors))
      .find((e): e is { message?: string } => !!e && typeof e === 'object' && 'message' in e);
    toast.error((first?.message && ERROR_COPY[first.message]) || t('layerStack.dialog.failedToStart'));
  };

  const onSubmit = (vars: LayerStackFormData) => {
    // The gate has the LAST word, re-read here rather than trusted from form
    // state or from the button's locked styling: a subscription can lapse
    // while the dialog is open, and this mutation costs real compute. The
    // button's own lock does not cover a form submitted with the Enter key.
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    mutation.mutate(vars);
  };

  if (sorted.length === 0) return <div className='p-4 text-sm'>{t('layerStack.dialog.noCandidates')}</div>;

  const blocked = derived.blocked || svDerived.blocked || stackSectionTooBig;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onInvalid)}
        className='flex max-h-[80vh] min-w-0 flex-col overflow-hidden'>
        <DialogHeader>
          <DialogTitle>{t('layerStack.dialog.title')}</DialogTitle>
          <DialogDescription>{t('layerStack.dialog.description')}</DialogDescription>
        </DialogHeader>
        <div className='flex min-w-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto py-4'>
          <LayerStackFields
            form={form}
            layers={sorted}
            levels={levels}
            fields={fields}
            derived={derived}
            warnings={warnings}
            gate={gate}
            onAddLevel={addLevel}
            onRemoveLevel={remove}
            levelErrors={levelErrors}
            stackErrors={stackErrors}
          />
          <FormField
            control={form.control}
            name='resolution'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('layerStack.dialog.resolution')}</FormLabel>
                <FormControl>
                  <NumericField
                    value={field.value}
                    onChange={field.onChange}
                    step='0.05'
                    min='0.01'
                    data-testid='stack-resolution'
                  />
                </FormControl>
                <FormDescription>{t('layerStack.dialog.resolutionHelp')}</FormDescription>
              </FormItem>
            )}
          />
          <StationVolumesFields
            values={svValues}
            onChange={(patch) => {
              if (patch.stationVolumesEnabled !== undefined)
                form.setValue('stationVolumesEnabled', patch.stationVolumesEnabled);
              if (patch.alignmentKey !== undefined) form.setValue('alignmentKey', patch.alignmentKey);
              if (patch.stationInterval !== undefined) form.setValue('stationInterval', patch.stationInterval);
              if (patch.swathLeft !== undefined) form.setValue('swathLeft', patch.swathLeft);
              if (patch.swathRight !== undefined) form.setValue('swathRight', patch.swathRight);
              if (patch.swathLinked !== undefined) form.setValue('swathLinked', patch.swathLinked);
              if (patch.stationFromText !== undefined) form.setValue('stationFromText', patch.stationFromText);
              if (patch.stationToText !== undefined) form.setValue('stationToText', patch.stationToText);
              if (patch.offsetBin !== undefined) form.setValue('offsetBin', patch.offsetBin);
              // Linked swath mirroring belongs to the form, not the field
              // component (same split as the other two dialogs).
              if (patch.swathLeft !== undefined && form.getValues('swathLinked'))
                form.setValue('swathRight', patch.swathLeft);
              if (patch.swathRight !== undefined && form.getValues('swathLinked'))
                form.setValue('swathLeft', patch.swathRight);
              if (patch.crossfallEnabled !== undefined) form.setValue('crossfallEnabled', patch.crossfallEnabled);
              if (patch.crossfallLeftEnabled !== undefined)
                form.setValue('crossfallLeftEnabled', patch.crossfallLeftEnabled);
              if (patch.crossfallRightEnabled !== undefined)
                form.setValue('crossfallRightEnabled', patch.crossfallRightEnabled);
              if (patch.crossfallLeftFrom !== undefined) form.setValue('crossfallLeftFrom', patch.crossfallLeftFrom);
              if (patch.crossfallLeftTo !== undefined) form.setValue('crossfallLeftTo', patch.crossfallLeftTo);
              if (patch.crossfallRightFrom !== undefined) form.setValue('crossfallRightFrom', patch.crossfallRightFrom);
              if (patch.crossfallRightTo !== undefined) form.setValue('crossfallRightTo', patch.crossfallRightTo);
              if (patch.crossfallLinked !== undefined) form.setValue('crossfallLinked', patch.crossfallLinked);
              if (patch.crossfallTolerancePct !== undefined)
                form.setValue('crossfallTolerancePct', patch.crossfallTolerancePct);
              // Linked bands mirror the way the linked swath does, and for the same
              // reason: the component reports ONE side, the form decides what that
              // means for the other, so all three dialogs get it from one place.
              if (form.getValues('crossfallLinked')) {
                if (patch.crossfallLeftFrom !== undefined) form.setValue('crossfallRightFrom', patch.crossfallLeftFrom);
                if (patch.crossfallLeftTo !== undefined) form.setValue('crossfallRightTo', patch.crossfallLeftTo);
                if (patch.crossfallRightFrom !== undefined)
                  form.setValue('crossfallLeftFrom', patch.crossfallRightFrom);
                if (patch.crossfallRightTo !== undefined) form.setValue('crossfallLeftTo', patch.crossfallRightTo);
              }
            }}
            resolution={resolution}
            derived={svDerived}
            gate={stationGate}
          />
          {svValues.stationVolumesEnabled && stackEstimate && (
            <p data-testid='stack-section-estimate' className='text-xs text-slate-600 dark:text-stone-400'>
              {t('layerStack.dialog.sectionEstimate', {
                planes: stackEstimate.planeCount,
                size: formatSectionBytes(stackEstimate.sectionBytes),
              })}
            </p>
          )}
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('layerStack.dialog.outputName')}</FormLabel>
                <FormControl>
                  <Input {...field} data-testid='stack-name' />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <DialogFooter>
          <Button
            data-testid='stack-submit'
            type='submit'
            // The HTML `disabled` attribute is for the DATA conditions only.
            // A gated user's button stays clickable, or the click that opens
            // the upgrade dialog never happens (CLAUDE.md).
            disabled={gate.allowed && (mutation.isPending || blocked)}
            aria-disabled={gate.allowed ? undefined : true}
            data-locked={gate.allowed ? undefined : 'true'}
            onClick={(e) => {
              if (!gate.allowed) {
                e.preventDefault();
                gate.promptUpgrade();
              }
            }}>
            {mutation.isPending ? t('layerStack.dialog.starting') : t('layerStack.dialog.run')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
